/**
 * The realtime contract. SPEC.md §15.4, §15.2.
 *
 * Two distinct broadcast channels, and the difference matters:
 *
 *  1. Sequenced domain mutations. Every one carries the session sequence it
 *     produced, and they are strictly contiguous. A client that sees 145 then
 *     148 knows it missed state and must resync. §15.2
 *
 *  2. Ephemeral presence (`player:connection-changed`). Deliberately carries no
 *     sequence, because a flapping mobile radio must not look like data loss or
 *     write a row per reconnect. §8.5, §16.1
 */

import type {
  AttachSessionInput,
  ClearHandEntryInput,
  CompleteRoundInput,
  MoveSeatInput,
  RecordDrinkInput,
  RemovePlayerInput,
  SelectDealerInput,
  SetHandEntryInput,
  SetLockedInput,
  StartRoundInput,
  UpdateProfileInput,
  VoidRoundInput,
} from './schemas.js';
import type { WireError } from './errors.js';
import type {
  Adjustment,
  EntryShape,
  HandEntry,
  PlayerPresence,
  PlayerView,
  Round,
  RoundSummary,
  Session,
  SessionSnapshot,
  TotalsPayload,
  Transfer,
} from './types.js';

// --- Event names -------------------------------------------------------------

export const CLIENT_EVENTS = {
  SESSION_JOIN: 'session:join',
  SESSION_RECONNECT: 'session:reconnect',
  SESSION_LOCK: 'session:lock',
  SESSION_UNLOCK: 'session:unlock',
  PLAYER_UPDATE_PROFILE: 'player:update-profile',
  PLAYER_MOVE_SEAT: 'player:move-seat',
  PLAYER_REMOVE: 'player:remove',
  ROUND_START: 'round:start',
  ROUND_COMPLETE: 'round:complete',
  ROUND_VOID: 'round:void',
  ROUND_SELECT_DEALER: 'round:select-dealer',
  HAND_ENTRY_SET: 'hand-entry:set',
  HAND_ENTRY_CLEAR: 'hand-entry:clear',
  DRINK_RECORD: 'drink:record',
  GAME_END: 'game:end',
  SNAPSHOT_REQUEST: 'session:request-snapshot',
} as const;

export const SERVER_EVENTS = {
  SESSION_SNAPSHOT: 'session:snapshot',
  SESSION_UPDATED: 'session:updated',
  PLAYER_JOINED: 'player:joined',
  PLAYER_UPDATED: 'player:updated',
  PLAYER_CONNECTION_CHANGED: 'player:connection-changed',
  PLAYER_SEAT_CHANGED: 'player:seat-changed',
  PLAYER_REMOVED: 'player:removed',
  ROUND_STARTED: 'round:started',
  ROUND_COMPLETED: 'round:completed',
  ROUND_VOIDED: 'round:voided',
  ROUND_DEALER_CHANGED: 'round:dealer-changed',
  HAND_ENTRY_CHANGED: 'hand-entry:changed',
  DRINK_RECORDED: 'drink:recorded',
  SYNC_REQUIRED: 'sync:required',
  ERROR: 'error',
} as const;

// --- Acknowledgements --------------------------------------------------------

/**
 * Every client→server event is acknowledged. Failures come back as an ack, not
 * as a thrown socket error, so the UI can show copy next to the control the
 * player actually touched.
 */
export type Ack<T> = { ok: true; data: T } | { ok: false; error: WireError };

export type AckFn<T> = (response: Ack<T>) => void;

export function ackOk<T>(data: T): Ack<T> {
  return { ok: true, data };
}

export function ackError(error: WireError): Ack<never> {
  return { ok: false, error };
}

// --- Sequenced payloads ------------------------------------------------------

/** Common shape of every sequenced broadcast. §15.2 */
export interface SequencedPayload {
  sequence: number;
}

/**
 * Where the run of sequences an event stands for begins. §15.2
 *
 * Most mutations produce exactly one sequence. Two do not — a report and a hand
 * deletion — because each takes one for itself and then one per row it caused,
 * and a row is a transfer *or*, in drinking mode, an adjustment. So the span is
 * recoverable from the payload, and this is the arithmetic that recovers it.
 *
 * It is shared because both ends have to agree on it exactly: the broadcaster
 * checks the span on the way out and the client checks it on the way in, and a
 * client that subtracted one array where the server subtracted two would read
 * every settled drinking hand as a gap and resync the whole table for nothing.
 * §60
 */
export function sequenceSpanStart(event: {
  sequence: number;
  transfers: readonly unknown[];
  adjustments: readonly unknown[];
}): number {
  return event.sequence - (event.transfers.length + event.adjustments.length);
}

export interface SessionSnapshotEvent {
  snapshot: SessionSnapshot;
}

export interface SessionUpdatedEvent extends SequencedPayload {
  session: Session;
}

export interface PlayerJoinedEvent extends SequencedPayload {
  player: PlayerView;
}

export interface PlayerUpdatedEvent extends SequencedPayload {
  player: PlayerView;
}

export interface PlayerSeatChangedEvent extends SequencedPayload {
  /** Only the seats that changed. §9.4 */
  seats: Array<{ playerId: string; seatIndex: number }>;
}

export interface PlayerRemovedEvent extends SequencedPayload {
  playerId: string;
}

export interface RoundStartedEvent extends SequencedPayload {
  round: Round;
}

export interface RoundCompletedEvent extends SequencedPayload {
  round: Round;
  summary: RoundSummary;
}

/**
 * One player's reported loss was set, amended or withdrawn. §3.1, §12.2
 *
 * One report can move a great deal more than one row. A TO_EVERYONE loss is a
 * row per opponent; the report that leaves exactly one player silent settles
 * *everybody's* pending losses at once; and withdrawing a report can un-infer a
 * winner and cancel all of them again. So this carries whatever the ledger
 * actually did, together with the recomputed totals — and `entries`, which is
 * the hand as it now stands and what every client re-derives the winner from.
 *
 * `entry` is this player's own report after the change, or null if they withdrew.
 */
export interface HandEntryChangedEvent extends SequencedPayload, TotalsPayload {
  roundId: string;
  /** The player the entry belongs to, not necessarily the device that sent it. */
  playerId: string;
  entry: { amount: number; shape: EntryShape } | null;
  /** Every report standing for the hand after this change. §3.1 */
  entries: HandEntry[];
  /** Rows written by this change, inverses included. */
  transfers: Transfer[];
  /** Rows superseded by it. §12.2 */
  reversedTransferIds: string[];
  /**
   * One-ended rows written by this change, inverses included. Empty at an
   * ordinary table; in drinking mode it is the winner's spill being written,
   * amended or taken back off. §4.6
   *
   * It counts towards the span this event covers, exactly as `transfers` does —
   * both ends work the span out as `sequence - (transfers + adjustments)`, so a
   * hand that produced one is not read as a gap. §15.2
   */
  adjustments: Adjustment[];
  /** Adjustments superseded by it. §12.2 */
  reversedAdjustmentIds: string[];
  /**
   * Whether the hand this report belongs to is still open. §4.3
   *
   * A report that decides the hand closes it in the same transaction, and a
   * closed hand has no report left to withdraw — which is exactly what the
   * snackbar has to know before it offers an UNDO that could not work. The
   * `round:completed` and `round:started` that follow carry the rest. §12.1
   */
  handOpen: boolean;
}

/**
 * The host moved the button to a named player. §9.6
 *
 * It carries the whole round rather than the chosen player, and that is the
 * point: what changed is the round's `dealerOffset`, and every device derives
 * the dealer from it with the same `dealerPlayerId` it always used. Sending a
 * player id instead would have put a second answer on the wire for a question
 * the client can already answer — and the two would disagree the first time
 * somebody was reseated between the write and the read. §60, §9.5
 *
 * Sequenced like any other domain mutation, and exactly one sequence wide: it
 * moves no points and writes no ledger row. §15.2
 */
export interface RoundDealerChangedEvent extends SequencedPayload {
  round: Round;
}

/**
 * The host deleted a hand. §12.5
 *
 * "Deleted" is what the table means and a void is what the ledger does: every
 * row the hand had derived is cancelled with an inverse, its reports are gone,
 * and the round itself stays on file carrying both halves — because a transfer
 * is never removed, only answered. §12.2
 *
 * It carries the live hand's reports because deleting the *open* hand empties
 * it in place, and the active round because deleting a closed one leaves it
 * untouched. Either way this is the state every client must land on.
 */
export interface RoundVoidedEvent extends SequencedPayload, TotalsPayload {
  round: Round;
  /** The inverses written to cancel it. §12.2 */
  transfers: Transfer[];
  /** The rows they cancelled. §12.2 */
  reversedTransferIds: string[];
  /** The inverses written to cancel its one-ended rows, drinks included. §4.6 */
  adjustments: Adjustment[];
  /** The adjustments they cancelled. §12.2 */
  reversedAdjustmentIds: string[];
  /** Reports standing for the live hand afterwards. §3.1 */
  handEntries: HandEntry[];
  /** The live hand's adjustments afterwards. §4.6 */
  handAdjustments: Adjustment[];
  /** The hand that is live now, which is where the table enters it again. */
  activeRound: Round | null;
}

/**
 * A player finished their drink. §4.6
 *
 * Drinking mode only, and the one mutation in the app that creates points rather
 * than moving them — so it is its own event rather than a transfer nobody could
 * find the other end of. One row, one sequence.
 *
 * It carries the player because every device plays the cheer for them, and the
 * recomputed totals because their score has just moved on all of them. §15.1
 */
export interface DrinkRecordedEvent extends SequencedPayload, TotalsPayload {
  roundId: string;
  /** Whose drink it was, not necessarily the device that recorded it. §30 */
  playerId: string;
  adjustment: Adjustment;
}

// --- Ephemeral payloads ------------------------------------------------------

/** No `sequence`: presence is not a sequenced mutation. §8.5 */
export interface PlayerConnectionChangedEvent {
  playerId: string;
  presence: PlayerPresence;
  lastSeenAt: string;
}

export type SyncRequiredReason =
  | 'sequence-gap'
  | 'server-restart'
  | 'integrity-check'
  | 'reattached';

export interface SyncRequiredEvent {
  reason: SyncRequiredReason;
  /** The server's current sequence, so the client can decide how far behind it is. */
  sequence: number;
}

// --- Socket.IO typed maps ----------------------------------------------------

export interface ServerToClientEvents {
  'session:snapshot': (payload: SessionSnapshotEvent) => void;
  'session:updated': (payload: SessionUpdatedEvent) => void;
  'player:joined': (payload: PlayerJoinedEvent) => void;
  'player:updated': (payload: PlayerUpdatedEvent) => void;
  'player:connection-changed': (payload: PlayerConnectionChangedEvent) => void;
  'player:seat-changed': (payload: PlayerSeatChangedEvent) => void;
  'player:removed': (payload: PlayerRemovedEvent) => void;
  'round:started': (payload: RoundStartedEvent) => void;
  'round:completed': (payload: RoundCompletedEvent) => void;
  'round:voided': (payload: RoundVoidedEvent) => void;
  'round:dealer-changed': (payload: RoundDealerChangedEvent) => void;
  'hand-entry:changed': (payload: HandEntryChangedEvent) => void;
  'drink:recorded': (payload: DrinkRecordedEvent) => void;
  'sync:required': (payload: SyncRequiredEvent) => void;
  error: (payload: WireError) => void;
}

export interface ClientToServerEvents {
  'session:join': (
    payload: AttachSessionInput,
    ack: AckFn<{ snapshot: SessionSnapshot }>,
  ) => void;
  'session:reconnect': (
    payload: AttachSessionInput,
    ack: AckFn<{ snapshot: SessionSnapshot | null; sequence: number }>,
  ) => void;
  'session:request-snapshot': (payload: null, ack: AckFn<{ snapshot: SessionSnapshot }>) => void;
  'session:lock': (payload: SetLockedInput, ack: AckFn<{ session: Session }>) => void;
  'session:unlock': (payload: SetLockedInput, ack: AckFn<{ session: Session }>) => void;
  'player:update-profile': (payload: UpdateProfileInput, ack: AckFn<{ player: PlayerView }>) => void;
  'player:move-seat': (
    payload: MoveSeatInput,
    ack: AckFn<{ seats: Array<{ playerId: string; seatIndex: number }> }>,
  ) => void;
  'player:remove': (payload: RemovePlayerInput, ack: AckFn<{ playerId: string }>) => void;
  'round:start': (payload: StartRoundInput, ack: AckFn<{ round: Round }>) => void;
  /** Host only, and only while a hand is in progress. §9.6, §5.1 */
  'round:select-dealer': (payload: SelectDealerInput, ack: AckFn<{ round: Round }>) => void;
  'round:complete': (
    payload: CompleteRoundInput,
    ack: AckFn<{ round: Round; summary: RoundSummary }>,
  ) => void;
  'round:void': (payload: VoidRoundInput, ack: AckFn<{ round: Round }>) => void;
  'hand-entry:set': (payload: SetHandEntryInput, ack: AckFn<HandEntryChangedEvent>) => void;
  'hand-entry:clear': (payload: ClearHandEntryInput, ack: AckFn<HandEntryChangedEvent>) => void;
  'drink:record': (payload: RecordDrinkInput, ack: AckFn<DrinkRecordedEvent>) => void;
  'game:end': (payload: null, ack: AckFn<{ session: Session }>) => void;
}

/** Per-socket state the server attaches after a successful attach. */
export interface SocketData {
  sessionId: string;
  playerId: string;
  isHost: boolean;
}

/** Socket.IO requires a fourth generic; this app has no inter-server events. */
export type InterServerEvents = Record<string, never>;
